import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { Search } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { env } from '@/constants/env';
import { cn } from '@/lib/utils';

import { roomModeSettingsAtom, settingsChangedAtom } from '../../atoms/settings';
import { ROOM_MODE_OPTIONS, TYPE_ROOM_MODE_OPTIONS } from '../../constants/style-settings';

export interface RoomModeSelectionContentProps {
  setPopoverOpen: Dispatch<SetStateAction<boolean>>;
}

const roomModeOptions = [
  {
    value: ROOM_MODE_OPTIONS.FURNISH_EMPTY_ROOM,
    image: 'default-imagerendering-mode_FillEmptyRoom',
  },
  { value: ROOM_MODE_OPTIONS.CHANGE_STYLE, image: 'default-imagerendering-mode_ChangeStyle' },
  {
    value: ROOM_MODE_OPTIONS.FURNITURE_REMOVAL,
    image: 'default-imagerendering-mode_EmptyFilledRoom',
  },
  { value: ROOM_MODE_OPTIONS.CLEAN_ROOM, image: 'default-imagerendering-room_KidsRoom' }, // TODO: Correct image, as soon as we have it
  {
    value: ROOM_MODE_OPTIONS.IMPROVE_IMAGE_QUALITY,
    image: 'default-imagerendering-mode_ImproveQuality',
  },
  { value: ROOM_MODE_OPTIONS.GOOD_WEATHER, image: 'default-imagerendering-mode_GoodWheater' },
];

export const RoomModeSelectionContent = ({ setPopoverOpen }: RoomModeSelectionContentProps) => {
  const [roomMode, setRoomMode] = useAtom(roomModeSettingsAtom);
  const [, setSettingsChanged] = useAtom(settingsChangedAtom);

  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>();

  const filteredRoomModeOptions = useMemo(
    () =>
      !search
        ? roomModeOptions
        : roomModeOptions.filter((option) =>
            t(option.value).toLowerCase().includes(search.toLowerCase())
          ),
    [t, search]
  );

  const handleSelect = (option: TYPE_ROOM_MODE_OPTIONS) => {
    setRoomMode(option);
    setPopoverOpen(false);
    setSettingsChanged(true);
  };

  return (
    <div className="w-full">
      <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 lg:h-[60px]">
        <span className="text-sm font-semibold text-white">{t('mode')}</span>
      </div>
      <div className="bg-white p-2 sm:px-5 sm:py-6">
        <Input
          size="sm"
          className="border border-solid border-primary-light"
          placeholder={t('search')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          startIcon={<Search className="text-primary-light" />}
        />
        <div className="mt-3 grid max-h-calc-full-minus-100 grid-cols-2 gap-2 overflow-y-auto sm:max-h-[400px] lg:max-h-[472px]">
          {filteredRoomModeOptions.map((option, index) => {
            const isSelected = option.value === roomMode;

            return (
              <div
                key={`desired-style-option-${index}`}
                className={cn(
                  'relative flex cursor-pointer flex-col overflow-hidden rounded-sm border border-solid',
                  !isSelected ? 'border-text-primary' : 'border-primary-light'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <div className="flex-1">
                  <img
                    className="h-[84px] w-full object-cover"
                    src={`${env.API_URL}/images/${option.image}?size=thumbnail500`}
                  />
                </div>
                <div
                  className={cn(
                    'z-2 flex h-[30px] items-center justify-center',
                    isSelected ? 'bg-primary-light' : 'bg-text-primary'
                  )}
                >
                  <span className="text-center text-sm font-semibold leading-none text-white">
                    {t(option.value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
